/** Static copy from the design. Everything data-driven (projects, media, templates,
 *  credits, avatar) comes from the API — see LandingPage.tsx. */

export const IMG = {
    logoLight: '/images/landing/logo-light.png',
    logoDark: '/images/landing/logo-dark.png',
    // Shown when the signed-in user has no avatar: mint disc + ink user glyph, in the design's palette.
    avatarDefault: '/images/landing/avatar-default.svg',
};

export interface Chip { label: string; dot: string; rot: number; prompt: string }

export const CHIPS: Chip[] = [
    { label: 'Product catalog', dot: '#E08B62', rot: -1.4, prompt: 'Build an online product catalog for my coffee shop — category filters, product detail pages, and a WhatsApp order button on every item.' },
    { label: 'Business profile', dot: '#2E9E64', rot: 1.2, prompt: 'Build a multi-page company profile site — home, services, about, and a contact page with a WhatsApp form.' },
    { label: 'Wedding invitation', dot: '#C77FA8', rot: -0.8, prompt: 'Build a one-page digital wedding invitation with a countdown, photo gallery, location map, and RSVP over WhatsApp.' },
    { label: 'Digital menu', dot: '#4C8FD6', rot: 1.5, prompt: 'Build a QR digital menu for my café — categories, photos, prices, and daily specials I can edit myself.' },
    { label: 'Service booking', dot: '#7A6FD1', rot: -1.1, prompt: 'Build a booking page for my service business — customers pick a date and time, fill in their details, and get confirmed over WhatsApp.' },
    { label: 'Event registration', dot: '#D9705B', rot: 0.9, prompt: 'Build an event registration page — ticket tiers, an attendee form, and a downloadable QR ticket.' },
    { label: 'Stock tracker', dot: '#3C9E9E', rot: -1.3, prompt: 'Build a simple stock tracker for my warehouse — item list, in and out entries, and a low-stock alert.' },
    { label: 'Class schedule', dot: '#B08A4A', rot: 1.1, prompt: 'Build a class schedule page for my course business — weekly timetable, teacher profiles, and sign-up over WhatsApp.' },
];

export interface Testimonial {
    quote: string; emphasis: string; name: string; meta: string;
    avatarImg: string; shotImg: string; caption: string;
}

export const TESTIMONIALS: Testimonial[] = [
    { quote: 'I typed one sentence about my warung on a Tuesday. By Wednesday customers were ordering from the link in my bio —', emphasis: 'no code needed.', name: 'Sari Anggraeni', meta: 'BANDUNG, INDONESIA', avatarImg: '/images/landing/hv-quote-avatar-0.webp', shotImg: '/images/landing/hv-quote-shot-0.webp', caption: 'LIVE IN 4 MIN' },
    { quote: 'Our wedding invite went from an idea to a working RSVP page before dinner —', emphasis: 'guests were replying the same night.', name: 'Patricia', meta: 'JAKARTA, INDONESIA', avatarImg: '/images/landing/hv-quote-avatar-1.webp', shotImg: '/images/landing/hv-quote-shot-1.webp', caption: 'LIVE IN 6 MIN' },
    { quote: 'I described my booking flow in plain Bahasa and it just built the form —', emphasis: 'no need to go through technical things.', name: 'Yoga Saputra', meta: 'SURABAYA, INDONESIA', avatarImg: '/images/landing/hv-quote-avatar-2.webp', shotImg: '/images/landing/hv-quote-shot-2.webp', caption: 'LIVE IN 5 MIN' },
];

export const MEDIA_ICONS: Record<string, string> = {
    Image: 'M4.2 5.4h15.6v13.2H4.2z M4.8 15.6l4.6-4.3 3.2 2.9 2.7-2.2 3.9 3.5 M8.6 10.2a1.05 1.05 0 1 0 0-2.1 1.05 1.05 0 0 0 0 2.1z',
    Vector: 'M3.8 3.8h4v4h-4z M16.2 16.2h4v4h-4z M7.8 5.8h6.4a2 2 0 0 1 2 2v8.4',
    Document: 'M6.2 3.4h7l4.6 4.6v12.6H6.2z M13.2 3.4v4.8h4.6 M9 13.4h6 M9 16.6h4',
    // Video is not in the design's icon set; a play-in-frame glyph in the same stroke style.
    Video: 'M4 6.5h16v11H4z M10 9.5v5l4.2-2.5z',
};

export const PLACEHOLDER_EXAMPLES = [
    'Example: a catalog for my coffee roastery — menu, our story, and a WhatsApp order button',
    'Example: a booking page for my hair salon with services, prices and a contact form',
    'Example: a one-page wedding invitation with RSVP and a location map',
    'Example: a portfolio for my photography with a contact page',
];


/** The "NEW" pill in the hero: its label and the catalog template it jumps to (ids come from core-api's /templates). */
// Floating "chat with us" button on every marketing page (LandingNav renders it).
export const WHATSAPP_CONTACT = { number: '6285121549837', message: 'Halo HyVay, saya ingin bertanya tentang HyVay.' };
export const whatsappLink = () => `https://wa.me/${WHATSAPP_CONTACT.number}?text=${encodeURIComponent(WHATSAPP_CONTACT.message)}`;

export const HERO_ANNOUNCEMENT ={ label: 'Klinik & Praktik Dokter', templateId: 'klinik-praktik' };

export const TOP_UP_OPTIONS = [25, 50, 100, 200];
export const PRICE_PER_CREDIT = 1000;
export const FEE_PERCENTAGE = 0.1;
/** Mirrors core-api: 1 credit per AI turn (ai-agent.service), 10 to publish (studio.service), 10 on signup (prisma default). */
export const CHAT_COST = 1;
export const PUBLISH_COST = 10;
export const SIGNUP_CREDITS = 10;

export const SUPPORT_EMAIL = 'support@hyvay.com';

/** Organisations shown in the landing's "Trusted by" strip; each links to its use case. */
export interface TrustedOrg { name: string; img: string; href: string; target: string; w: number; h: number }
export const TRUSTED_BY: TrustedOrg[] = [
    { name: 'Fakultas Ilmu Komputer, Universitas Indonesia', img: '/images/landing/trusted/fasilkom-ui.webp', href: 'https://cs.ui.ac.id/', target: '_blank', w: 2560, h: 931 },
];

/** mailto: for the custom-application package, pre-filled so support gets a usable brief. */
export function customAppMailto(context?: string): string {
    const subject = 'HyVay custom application — quote request';
    const body = [
        'Hi HyVay team,',
        '',
        'I would like a quote for a custom application.',
        context ? `Reference: ${context}` : '',
        '',
        'Name / organisation:',
        '',
        'Type of application (web / desktop / mobile, one or more):',
        '',
        'What the app should do (users, data, admin needs):',
        '',
        'Target launch date:',
        '',
    ].filter((l, i, a) => !(l === '' && a[i - 1] === '')).join('\n');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export const FONT_SANS = "'Plus Jakarta Sans',system-ui,sans-serif";
export const FONT_SERIF = "'Instrument Serif',Georgia,serif";
export const FONT_MONO = "'Space Mono',monospace";
